import type {
  ConditionOption,
  LabeledOption,
} from "@/shared/components/filters/FilterDefinition";
import { IngestionLabel, IngestionStatus } from "@/shared/domain/files";

import type { StaticFilterField } from "./StaticFilterField";

const NOT_INGESTED = {
  field: "ingestion_status",
  comparator: "EQUALS",
  value: IngestionStatus.NotIngested,
} as const;

/**
 * The Ingestion filter's options: the labels the files table's Ingestion column
 * shows from a file record alone (`RECORD_INGESTION_LABELS`), from the same
 * constants. The column's other labels, "Ingesting…" and "Ingestion failed",
 * come from each file's latest run, which the files query cannot filter on, so
 * no option offers them: a file the filter selects as "Awaiting ingestion" or
 * "Partly ingested" can show either in its cell.
 *
 * `ingestion_status` alone cannot tell a file waiting on ingestion from one no
 * rule matches, so those two options stand for a condition on `ingestable`
 * too. Listed most-ingested first. "Partly ingested" stays its own option:
 * `partly_ingested` means a topic was recorded against the file, while
 * `ingested` means completion was reported, which is what post-ingestion
 * triggers wait on.
 */
export const INGESTION_LABEL_OPTIONS: readonly (
  | LabeledOption
  | ConditionOption
)[] = [
  { label: IngestionLabel.Ingested, value: IngestionStatus.Ingested },
  {
    label: IngestionLabel.PartlyIngested,
    value: IngestionStatus.PartlyIngested,
  },
  {
    label: IngestionLabel.AwaitingIngestion,
    value: "awaiting_ingestion",
    condition: {
      operator: "AND",
      conditions: [
        NOT_INGESTED,
        { field: "ingestable", comparator: "EQUALS", value: true },
      ],
    },
  },
  {
    label: IngestionLabel.StoredAsIs,
    value: "stored_as_is",
    condition: {
      operator: "AND",
      conditions: [
        NOT_INGESTED,
        { field: "ingestable", comparator: "EQUALS", value: false },
      ],
    },
  },
];

/** The files table's filter rows, in picker order. */
export const FILE_FILTER_FIELDS = [
  { type: "string", field: "file_id", name: "File Id" },
  { type: "string", field: "name", name: "Name" },
  { type: "string", field: "relative_path", name: "Relative Path" },
  { type: "string", field: "description", name: "Description" },
  { type: "string", field: "uri", name: "URI" },
  { type: "string", field: "device_id", name: "Device Id" },
  { type: "string", field: "origination", name: "Source" },
  { type: "string", field: "upload_id", name: "Upload Id" },
  { type: "string", field: "association_id", name: "Association Id" },
  { type: "date", field: "created", name: "Created" },
  { type: "identity", field: "created_by", name: "Created By" },
  { type: "date", field: "modified", name: "Modified" },
  { type: "identity", field: "modified_by", name: "Modified By" },
  { type: "numeric", field: "size", name: "Size" },
  {
    type: "enum",
    field: "ingestion_status",
    name: "Ingestion",
    options: INGESTION_LABEL_OPTIONS,
    // `fs_nodes.ingestion_status` is NOT NULL, so IS_NULL would match no
    // file and IS_NOT_NULL every one. Neither belongs in the menu.
    nullable: false,
  },
  { type: "set", field: "tags", name: "Tags" },
] as const satisfies readonly StaticFilterField[];

/** Each `ingestion_status` as the files table shows it while `release-ingestion-labels` is off. */
export const INGESTION_STATUS_LABELS: Record<IngestionStatus, string> = {
  [IngestionStatus.Ingested]: "Ingested",
  [IngestionStatus.PartlyIngested]: "Partly ingested",
  [IngestionStatus.NotIngested]: "Not ingested",
};

/**
 * `FILE_FILTER_FIELDS` with an Ingestion Status filter that offers each
 * `ingestion_status`, most-ingested first, rather than the ingestion labels.
 * The files table's filters while `release-ingestion-labels` is off.
 */
export const FILE_FILTER_FIELDS_BY_STATUS: readonly StaticFilterField[] =
  FILE_FILTER_FIELDS.map((field) =>
    field.field === "ingestion_status"
      ? {
          ...field,
          name: "Ingestion Status",
          options: [
            IngestionStatus.Ingested,
            IngestionStatus.PartlyIngested,
            IngestionStatus.NotIngested,
          ].map((value) => ({ label: INGESTION_STATUS_LABELS[value], value })),
        }
      : field,
  );
